import { useEffect, useState } from 'react';

import { StatusBadge } from './StatusBadge';
import { Button } from './ui';

type BackupState = 'checking' | 'ok' | 'backup-overdue' | 'backup-unverified' | 'unreachable';

/** Report the remote-backup check independently from ordinary workspace sync. */
export function BackupStatus() {
  const [state, setState] = useState<BackupState>('checking');
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const abort = new AbortController();

    setState('checking');

    const timeout = setTimeout(() => {
      abort.abort();
      setState('unreachable');
    }, 15000);

    void fetch('/api/health/backup', { cache: 'no-store', signal: abort.signal })
      .then(async response => {
        const body = await response.json();

        if (response.status === 200 && body.status === 'ok') {
          return 'ok' as const;
        }

        if (
          response.status === 503 &&
          (body.status === 'backup-overdue' || body.status === 'backup-unverified')
        ) {
          return body.status as BackupState;
        }

        return 'unreachable' as const;
      })
      .then(result => {
        if (!abort.signal.aborted) {
          setState(result);
        }
      })
      .catch(() => {
        if (!abort.signal.aborted) {
          setState('unreachable');
        }
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      clearTimeout(timeout);
      abort.abort();
    };
  }, [refresh]);

  const messages: Record<BackupState, { title: string; detail: string }> = {
    checking: {
      title: 'Checking backup status',
      detail: 'Checking the last off-server backup recorded by the server.',
    },
    ok: {
      title: 'Recent off-server backup',
      detail:
        'The last remote backup job succeeded within 24 hours. A restore exercise is still needed to verify recovery.',
    },
    'backup-overdue': {
      title: 'Backup needs attention',
      detail:
        'The last remote backup failed or is more than 24 hours old. Check the scheduled job and storage destination.',
    },
    'backup-unverified': {
      title: 'No off-server backup verified',
      detail:
        'No successful remote backup is recorded. Configure a backup destination and run the first backup.',
    },
    unreachable: {
      title: 'Backup status unavailable',
      detail: 'Reconnect and check again. This does not mean a backup has succeeded.',
    },
  };

  return (
    <section className="detail-card" aria-label="Off-server backup status">
      <h2>Backups & recovery</h2>
      <div role="status">
        <StatusBadge
          tone={state === 'ok' ? 'success' : state === 'checking' ? 'neutral' : 'attention'}
        >
          {messages[state].title}
        </StatusBadge>
      </div>
      <p>{messages[state].detail}</p>
      <p>
        “Synced” means this device’s changes reached Cairn. Backups protect against losing the
        server and are checked separately.
      </p>
      <Button
        variant="secondary"
        disabled={state === 'checking'}
        onClick={() => setRefresh(value => value + 1)}
      >
        Check backup status
      </Button>
    </section>
  );
}
